import { Card, Col, Form, Input, Radio, Row, type FormInstance } from 'antd'
import { MAX_WHOLE_QUANTITY } from '@repo/shared'
import { useTranslation } from 'react-i18next'

import { CategorySelect } from '../../components/CategorySelect'
import {
  KrwAmountInput,
  QuantityInput,
  wholeKrwAmountRule,
  wholeQuantityRule,
} from '../../components/number-input'
import { ProductImageUpload } from './ProductImageUpload'
import type { ProductCreateFormValues } from './ProductCreateFormTypes'

interface ProductCreateFormProps {
  form: FormInstance<ProductCreateFormValues>
  onImageUpload: (file: File) => Promise<string>
  isUploading: boolean
  initialValues?: Partial<ProductCreateFormValues>
  isEditMode?: boolean
}

export function ProductCreateForm({
  form,
  onImageUpload,
  isUploading,
  initialValues,
  isEditMode,
}: ProductCreateFormProps) {
  const { t } = useTranslation()
  const defaults = { salesChannel: 'MARKETPLACE' as const, shippingFee: 0, ...initialValues }

  return (
    <Form form={form} layout="vertical" preserve={false} initialValues={defaults}>
      <Row gutter={[16, 16]} style={{ maxWidth: 900, margin: '0 auto' }}>
        <Col span={24}>
          <Card title={t('pages.products.sections.basicInfo')} size="small">
            <Form.Item
              name="name"
              label={t('common.formLabels.productName')}
              rules={[
                { required: true, message: t('pages.products.validation.nameRequired') },
                { max: 200, message: t('pages.products.validation.nameMaxLength', { max: 200 }) },
              ]}
            >
              <Input
                data-testid="product-form-name-input"
                placeholder={t('pages.products.placeholders.productName')}
              />
            </Form.Item>

            <Form.Item
              name="description"
              label={t('common.formLabels.description')}
              rules={[
                {
                  max: 2000,
                  message: t('pages.products.validation.descriptionMaxLength', { max: 2000 }),
                },
              ]}
            >
              <Input.TextArea
                data-testid="product-form-description-input"
                rows={4}
                placeholder={t('pages.products.placeholders.description')}
                showCount
                maxLength={2000}
              />
            </Form.Item>

            <Form.Item
              name="productCategoryIds"
              label={t('common.formLabels.category')}
              rules={[{ required: true, message: t('pages.products.validation.categoryRequired') }]}
            >
              <CategorySelect
                data-testid="product-form-category-select"
                style={{ width: '100%' }}
              />
            </Form.Item>
          </Card>
        </Col>

        <Col xs={24} lg={12}>
          <Card title={t('pages.products.sections.priceInventory')} size="small">
            <Form.Item
              name="price"
              label={t('common.formLabels.price')}
              rules={[
                { required: true, message: t('pages.products.validation.priceRequired') },
                {
                  type: 'number',
                  min: 0,
                  message: t('pages.products.validation.priceNonNegative'),
                },
                wholeKrwAmountRule(t('pages.products.validation.priceWhole')),
              ]}
            >
              <KrwAmountInput
                data-testid="product-form-price-input"
                style={{ width: '100%' }}
                placeholder={t('pages.products.placeholders.price')}
              />
            </Form.Item>

            <Form.Item
              name="shippingFee"
              label={t('common.formLabels.shippingFee')}
              rules={[
                { required: true, message: t('pages.products.validation.shippingFeeRequired') },
                {
                  type: 'number',
                  min: 0,
                  message: t('pages.products.validation.shippingFeeNonNegative'),
                },
                wholeKrwAmountRule(t('pages.products.validation.shippingFeeWhole')),
              ]}
            >
              <KrwAmountInput
                data-testid="product-form-shipping-fee-input"
                style={{ width: '100%' }}
                placeholder={t('pages.products.placeholders.shippingFee')}
              />
            </Form.Item>

            <Form.Item
              name="quantity"
              label={t('common.formLabels.quantity')}
              rules={[
                { required: true, message: t('pages.products.validation.quantityRequired') },
                wholeQuantityRule(
                  t('pages.products.validation.quantityInteger', {
                    max: MAX_WHOLE_QUANTITY.toLocaleString(),
                  }),
                ),
                {
                  type: 'number',
                  min: 0,
                  message: t('pages.products.validation.quantityNonNegative'),
                },
              ]}
            >
              <QuantityInput
                data-testid="product-form-quantity-input"
                style={{ width: '100%' }}
                placeholder={t('pages.products.placeholders.quantity')}
              />
            </Form.Item>
          </Card>
        </Col>

        <Col xs={24} lg={12}>
          <Card title={t('pages.products.sections.salesChannel')} size="small">
            <Form.Item
              name="salesChannel"
              label={t('common.formLabels.salesChannel')}
              rules={[
                { required: true, message: t('pages.products.validation.salesChannelRequired') },
              ]}
            >
              <Radio.Group data-testid="product-form-channel-radio" disabled={isEditMode}>
                <Radio data-testid="product-form-channel-marketplace" value="MARKETPLACE">
                  {t('common.status.marketplace')}
                </Radio>
                <Radio data-testid="product-form-channel-broadcast" value="BROADCAST">
                  {t('common.status.broadcast')}
                </Radio>
              </Radio.Group>
            </Form.Item>
          </Card>
        </Col>

        <Col span={24}>
          <Card title={t('pages.products.sections.images')} size="small">
            <Form.Item
              name="images"
              rules={[{ required: true, message: t('pages.products.validation.imagesRequired') }]}
            >
              <ProductImageUpload onUpload={onImageUpload} isUploading={isUploading} />
            </Form.Item>
          </Card>
        </Col>
      </Row>
    </Form>
  )
}
